import { IconButton } from '@material-ui/core';
import { isKeyedAxiosError } from 'app/core/api/api-core/api-core.types';
import { useGetProductByUpc } from 'app/core/api/product/api-product';
import { getErrorMsg } from 'app/core/errors/error-utils';
import Button from 'app/shared/components/button/Button';
import { BasicDialogOpts } from 'app/shared/components/dialog/BasicDialog';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import Scanner, {
  VideoCommand,
} from 'app/shared/components/scanner/Scanner';
import { CodeRead } from 'app/shared/components/scanner/utilities/scan-utils';
import Body from 'app/shared/components/typography/body/Body';
import { InstructionsState } from 'app/shared/context-hooks/use-instructions.hook';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useViewport } from 'app/shared/context-hooks/use-viewport.hook';
import LoadingOverlay from 'app/shared/page-parts/LoadingOverlay';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { QuaggaBarcodeType } from 'app/tools/general.types';
import {
  useEffectOnce,
  useObservableSend,
} from 'app/tools/hooks/misc-hooks';
import { Markup } from 'interweave';
import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { finalize } from 'rxjs/operators';
import styled from 'styled-components/macro';
import ConsumerRoutePath from '../../ConsumerRoutePath';

type ScanWindow = {
  size: number;
  top: number;
  left: number;
  shadowSpread: number;
};

// TODO: add cropping back in for landscape camera support
type SizeOpts = {
  $vh: (n: number) => number;
  $vw: (n: number) => number;
  $scanWindow: ScanWindow;
};

const cornerGradient = (
  side: 'top' | 'left' | 'bottom' | 'right',
  vals: [0 | 1, 0 | 1]
) =>
  `linear-gradient(to ${side}, white 4px, transparent 4px) ${vals
    .map(v => (v ? '100%' : '0'))
    .join(' ')}`;

const InstructionText = styled(Body)``;

const Overlay = styled.div<SizeOpts>`
  ${styledMixins.fullsize}
  ${styledMixins.flexCenter}
  justify-content: flex-end;
  padding: ${styledFuncs.pad(1.5)};

  position: absolute;
  overflow: hidden;

  > * {
    margin: ${styledFuncs.pad()};
  }

  > ${InstructionText} {
    position: absolute;
    bottom: ${p => p.$vh(100) - p.$scanWindow.top + 3}px;
  }

  &::before {
    content: '';
    position: absolute;

    // window height is set to 2/3 of width
    height: ${p => (p.$scanWindow.size * 2) / 3}px;
    width: ${p => p.$scanWindow.size}px;
    top: ${p => p.$scanWindow.top}px;
    left: ${p => p.$scanWindow.left}px;

    // add the translucent grey background around the scan window
    box-shadow: 0px 0px 0px ${p => p.$scanWindow.shadowSpread}px #0007;
  }
  &::after {
    content: '';
    position: absolute;

    --b: 4px; /* thickness of the border */
    --w: 22px; /* length of border */

    height: calc(${p => (p.$scanWindow.size * 2) / 3}px + var(--b));
    width: calc(${p => p.$scanWindow.size}px + var(--b));
    top: calc(${p => p.$scanWindow.top}px - var(--b) / 2);
    left: calc(${p => p.$scanWindow.left}px - var(--b) / 2);

    // adding the corners to the scan window
    background: ${cornerGradient('right', [0, 0])},
      ${cornerGradient('right', [0, 1])},
      ${cornerGradient('left', [1, 0])},
      ${cornerGradient('left', [1, 1])},
      ${cornerGradient('bottom', [0, 0])},
      ${cornerGradient('bottom', [1, 0])},
      ${cornerGradient('top', [0, 1])},
      ${cornerGradient('top', [1, 1])};
    background-repeat: no-repeat;
    background-size: var(--w) var(--w);
  }
`;

const WhiteButton = styled(Button)`
  color: white;
  &,
  &:hover {
    border-color: white;
  }
`;

const errorDialogs = {
  NotAllowedError: [
    "You'll need to allow camera access to scan barcodes.",
    <>
      Alternatively, you can search for your product{' '}
      <Link to={ConsumerRoutePath.PRODUCT_SEARCH_CATALOG}>here</Link>.
    </>,
  ],
};

const allowedFormats = [
  QuaggaBarcodeType.EAN_8,
  QuaggaBarcodeType.EAN,
  QuaggaBarcodeType.UPC,
  QuaggaBarcodeType.UPC_E,
];

export default function ProductScan(): JSX.Element {
  const scanSentRef = useRef(false);
  const history = useHistory();

  const { showDialog, showInstruction } = useShowInfo();
  const { vWidth, vHeight, vw, vh } = useViewport();

  const { request$: lookupProduct$, sending: searchingBarcode } =
    useGetProductByUpc();

  const [cameraError, setCameraError] = useState<string | null>(null);
  const [sendCommand, command$] = useObservableSend<VideoCommand>();

  useEffectOnce(() =>
    showInstruction({
      instructionName: 'scanInstructions',
      title: "Let's get scanning",
      noShowAfterDismiss: true,
      message: [
        'Point your camera at the product barcode to scan it and search for a match.',
        "If you can't scan the barcode, choose one of the other options below.",
      ],
      actions: [{ text: 'Got it' }],
    })
  );

  const showErrorDialog = (
    instructionName: keyof InstructionsState,
    message: BasicDialogOpts['message'],
    onClose?: () => void
  ) =>
    showInstruction({
      instructionName,
      message,
      actions: [
        {
          text: "Don't show again",
          dontShowAgain: true,
          callback: onClose,
        },
        {
          text: 'Close',
          callback: onClose,
        },
      ],
    });

  const handleScan = (codeScan: CodeRead) => {
    if (!codeScan.code || scanSentRef.current) return;

    sendCommand('pause');

    // NOTE: not supported in PWA on iOS
    try {
      window.navigator?.vibrate(60);
    } catch {}

    lookupProduct$(codeScan.code)
      .pipe(finalize(() => sendCommand('play')))
      .subscribe({
        complete: () => {
          history.push(
            ConsumerRoutePath.PRODUCT_DATABASE_LOOKUP_RESULT
          );
        },
        error: (e: unknown) => {
          if (isKeyedAxiosError(e)) {
            if (e.code === '404') {
              history.push(
                ConsumerRoutePath.PRODUCT_DATABASE_LOOKUP_FAILED
              );
            } else {
              showDialog({
                title: 'Barcode lookup error',
                message: (
                  <Markup
                    noWrap
                    content={e.data.detail || e.message}
                  />
                ),
                onDismissed: () => {
                  scanSentRef.current = false;
                },
                actions: [{}], // use default action button (close)
              });
            }
            return;
          }
          showErrorDialog(
            'cameraErrors',
            [getErrorMsg(e, 'Error: Invalid barcode')],
            () => {
              scanSentRef.current = false;
            }
          );
        },
      });
    scanSentRef.current = true;
  };

  const vs = (u: number) => Math.min(vh(u), vw(u));
  const vl = (u: number) => Math.max(vh(u), vw(u));

  const size = Math.min(vs(80), vl(40));
  const scanWindow = {
    size,
    top: vh(50) - size / 3, // height reduced to 2/3, so top offset is 1/3
    left: vw(50) - size / 2, // width still at full value, left offset is 1/2
    shadowSpread: vl(50) + vs(50) - size / 3,
  };

  const sizeOpts: SizeOpts = {
    $vw: vw,
    $vh: vh,
    $scanWindow: scanWindow,
  };

  const windowDimensions = useMemo(
    () => ({
      width: vWidth,
      height: vHeight,
      // NOTE: assumes scan window is centralised
      scanWindowSize: scanWindow.size,
    }),
    [scanWindow.size, vHeight, vWidth]
  );

  const scanOptions = useMemo(
    () => ({ barcodeTypes: allowedFormats }),
    []
  );

  return (
    <div className="flex relative flex-col justify-center w-full flex-fit-full">
      <Scanner
        {...{
          windowDimensions,
          scanOptions,
          command$,
        }}
        onDataUpdate={scanResult => {
          if (scanResult) handleScan(scanResult);
        }}
        onStreamError={err => {
          const errorDialogFound = _(errorDialogs)
            .toPairs()
            .find(([key]) => String(err).includes(key))?.[1];

          if (errorDialogFound) {
            setCameraError(String(errorDialogFound[0]));
            showErrorDialog('cameraPermissions', errorDialogFound);
          } else {
            setCameraError(String(err));
            showErrorDialog('cameraErrors', [String(err)]);
          }
        }}
      />
      {searchingBarcode && (
        <LoadingOverlay translucent noPortal disableShrink />
      )}
      <Overlay {...sizeOpts}>
        <IconButton
          aria-label="close"
          onClick={() => history.goBack()}
          className="absolute top-0 right-0 text-white"
        >
          <Icon icon={IconLib.CROSS_BOXED} size={38} />
        </IconButton>
        <InstructionText
          className={cameraError ? 'text-neutral-500' : 'text-white'}
          centered
          small
        >
          {cameraError || 'Point at barcode to start scanning!'}
        </InstructionText>
        <WhiteButton
          to={ConsumerRoutePath.PRODUCT_ENTER_BARCODE}
          startIcon={IconLib.EDIT}
          variant="outlined"
        >
          Enter barcode manually
        </WhiteButton>
      </Overlay>
    </div>
  );
}
